import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useRef } from 'react';

import { syncNow, useSyncState } from './controller';
import { supportsFolderSync } from './folder';
import { completeICloudSignIn, icloudAvailable } from './icloud';
import { dataFingerprint } from './sync';

/** How long after the last change before this device's file is rewritten. */
export const WRITE_DELAY_MS = 15_000;
/** How often an open, visible app looks for other devices' changes. */
export const POLL_INTERVAL_MS = 5 * 60_000;
/** Coming back to the window re-reads the folder, but not more often than this. */
const FOCUS_MIN_INTERVAL_MS = 60_000;

/**
 * Keeps the sync folder current while the app is open. Mounted once, in the
 * root layout.
 *
 * Three triggers: a change to the data (debounced, so a burst of edits in the
 * logbook grid is one write), the window becoming visible again (the likeliest
 * moment another device has written), and a slow poll for a window left open.
 * All three go through `syncNow`, which never runs two at once and never
 * prompts for permission.
 */
export function useAutoSync(): void {
  const supported = supportsFolderSync() || icloudAvailable();
  // Active while either target can run unattended.
  const active = useSyncState(
    (state) =>
      supported &&
      [state.folder.phase, state.icloud.phase].some(
        (phase) => phase !== 'off' && phase !== 'needs-permission',
      ),
  );
  // Only while a folder is in use: the fingerprint reads every flight and
  // entry, and re-runs on every change to the database.
  const fingerprint = useLiveQuery(() => (active ? dataFingerprint() : ''), [active]);
  const lastRun = useRef(0);

  useEffect(() => {
    if (supported) {
      lastRun.current = Date.now();
      // A page load that is Apple sending the pilot back from iCloud sign-in
      // finishes that first, so the very first sync already has the token.
      void completeICloudSignIn()
        .catch(() => false)
        .then(() => syncNow());
    }
  }, [supported]);

  useEffect(() => {
    if (!active || fingerprint === undefined) {
      return;
    }
    const timer = setTimeout(() => {
      lastRun.current = Date.now();
      void syncNow();
    }, WRITE_DELAY_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [active, fingerprint]);

  useEffect(() => {
    if (!active) {
      return;
    }
    const run = () => {
      if (document.visibilityState === 'visible') {
        lastRun.current = Date.now();
        void syncNow();
      }
    };
    const onVisible = () => {
      if (Date.now() - lastRun.current > FOCUS_MIN_INTERVAL_MS) {
        run();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    const interval = setInterval(run, POLL_INTERVAL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(interval);
    };
  }, [active]);
}
